import React, { useState, useRef, useEffect } from 'react';
import store from '@apply/adminStore';
import { GenericPopup, ButtonTheme, SignaturePopup, ValidationMessagesPopup } from '@apply/components';
import { AddReferralPanel } from './components/AddReferralPanel/AddReferralPanel';
import { useBrokerNewReferralActions } from './queries/useBrokerNewReferralActions';
import { useBrokerICCAAgreementActions } from './queries/useBrokerICCAAgreementActions';
import { useBrokerNewReferralStore } from './state/useBrokerNewReferralStore';
import { handleAdminError, handleAdminSuccess } from '../../../../common/SharedFunctions';
import { ValidationGroup } from 'devextreme-react';
import { LoadPanel } from 'devextreme-react/load-panel';
import parse from 'html-react-parser';
import { isMobileScreen } from '@apply/ui';

interface AddReferralModalV2Props {
    isOpen: boolean;
    cancelCallback?: () => void;
    submitCallback?: () => void;
}

export const AddReferralModalV2: React.FC<AddReferralModalV2Props> = ({ isOpen, cancelCallback, submitCallback }) => {
    const mobileScreen = isMobileScreen();
    const { user } = store.getState();
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('');
    const [validation, set_validation] = useState();
    const rgBrokerAddReferralRef = useRef<ValidationGroup>(null);
    const [requiredFieldsValid, set_requiredFieldsValid] = useState(false);

    const [brokerAgreementHtml, set_brokerAgreementHtml] = useState();
    const [openAcceptIccaModal, set_openAcceptIccaModal] = useState(false);

    const referralActions = useBrokerNewReferralActions();
    const iccaActions = useBrokerICCAAgreementActions();
    const FONT_ID = 1;

    const set_brokerLicenseNumber = useBrokerNewReferralStore((s) => s.set_brokerArizonaLicenseNumber);

    const getBrokerLicenseNumber = async () => {
        const response = await referralActions.getBrokerLicenseNumberAsync(user.broker.id);
        if (!response) {
            return;
        }
        const licenseNumber = response.data?.result;
        set_brokerLicenseNumber(licenseNumber);
    };

    useEffect(() => {
        if (!isOpen) {
            referralActions.reset();
            set_loadingOpen(false);
            set_loadingMessage('');
            return;
        }

        const loadBrokerLicenseNumber = async () => {
            set_loadingOpen(true);
            set_loadingMessage('Loading Broker Settings...');

            await getBrokerLicenseNumber();

            set_loadingOpen(false);
            set_loadingMessage('');
        };

        loadBrokerLicenseNumber();
    }, [isOpen]);

    const onValidationCancel = () => {
        set_validation(undefined);
    };

    const iccaAgreementCheckboxChanged = async () => {
        // Inject broker id before saving
        useBrokerNewReferralStore.getState().set_brokerId(user.broker.id);

        const response = await iccaActions.signICCAAgreementAsync();
        if (!response) {
            return;
        }

        const html = response.data?.result;
        set_brokerAgreementHtml(html);
        set_openAcceptIccaModal(true);
    };

    const handleICCASignConfirm = () => {
        useBrokerNewReferralStore.getState().set_fontId(FONT_ID);
        useBrokerNewReferralStore.getState().set_iccaAgreement(true);
        set_openAcceptIccaModal(false);
    };

    const handleICCASignCancel = () => {
        useBrokerNewReferralStore.getState().set_iccaAgreement(false);
        set_openAcceptIccaModal(false);
    };

    const handleCancel = () => {
        if (cancelCallback) {
            cancelCallback();
        }
    };

    const handleSuccessAndClose = () => {
        if (submitCallback) {
            submitCallback();
        }
    };

    const handleSubmit = async () => {
        const isOk = await validateReferral();
        if (!isOk) {
            return;
        }
        saveReferral();
    };

    const validateReferral = async () => {
        //Validate names
        set_loadingMessage('Validating names and email...');
        set_loadingOpen(true);
        try {
            const result = await referralActions.validateAsync();
            if (!result.ok) {
                // API threw or network error
                handleAdminError(result.error, 'An unexpected error has occurred! Please try again.');
                set_loadingOpen(false);
                return false;
            }
            const response = result.data; // Handle validation errors/warnings exactly like before
            if (response && (response.result.Status || response.result.hasWarnings)) {
                set_validation(response.result);
                set_loadingOpen(false);
                return false;
            }

            useBrokerNewReferralStore.getState().set_clientIsFlagged(false);
            return true;
        } catch (ex) {
            set_loadingOpen(false);
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
            return false;
        } finally {
            set_loadingOpen(false);
        }
    };

    const saveReferral = async () => {
        try {
            set_loadingMessage('Saving referral...');
            set_loadingOpen(true);

            const result = await referralActions.saveAsync();
            if (!result.ok) {
                // API threw or network error
                handleAdminError(result.error, 'An unexpected error has occurred! Please try again.');
                set_loadingOpen(false);
                return false;
            }
            const response = result.data; // Handle validation errors/warnings exactly like before
            if (response && (response.result.hasErrors || response.result.hasWarnings)) {
                set_validation(response.result);
                set_loadingOpen(false);
                return false;
            }
            handleAdminSuccess('Referral saved!');
            handleSuccessAndClose();
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred! Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const actionButtons = [
        {
            id: 'cancelAddReferralButton',
            theme: ButtonTheme.LinkBlue,
            disabled: false,
            children: 'Cancel',
            width: '150px',
            handleClick: handleCancel,
        },
        {
            id: 'submitAddReferralButton',
            theme: ButtonTheme.Primary,
            disabled: !requiredFieldsValid,
            children: 'Submit',
            width: '200px',
            handleClick: handleSubmit,
        },
    ];

    return (
        <>
            <GenericPopup
                id='newBrokerReferralPopup'
                key={isOpen ? 'openNewBrokerPopup' : 'closedNewBrokerPopup'}
                isOpen={isOpen}
                actionButtons={actionButtons}
                handleClose={handleCancel}
                aria-labelledby='alert-dialog-title'
                aria-describedby='alert-dialog-description'
                hideOnOutsideClick={false}
                showCloseButton={true}
                showTitle={true}
                title='Add Referral'
                width='750px'
                maxWidth='98%'
                maxHeight='98%'
                buttonAlignment='right'
                wrapTitle={false}
            >
                <ValidationGroup
                    id='vgBrokerAddReferral'
                    ref={rgBrokerAddReferralRef}
                >
                    <AddReferralPanel
                        onValidationChange={(isValid) => set_requiredFieldsValid(isValid)}
                        onICCAAgreementChange={iccaAgreementCheckboxChanged}
                        isOpen={isOpen}
                    />
                </ValidationGroup>
            </GenericPopup>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <ValidationMessagesPopup
                id={'addNewBrokerMessageValidationPopup'}
                isOpen={!!validation}
                validations={validation}
                onCancel={onValidationCancel}
                title='Referral Warning'
                minHeight='200px'
                showConfirm={false}
                cancelLabel='OK'
            />
            <SignaturePopup
                id={'signICCAPopup'}
                onSubmitAgreement={handleICCASignConfirm}
                onDeclineAgreement={handleICCASignCancel}
                isOpen={openAcceptIccaModal}
                firstName={user.firstName}
                lastName={user.lastName}
                width={mobileScreen ? '98%' : '800px'}
                maxWidth={mobileScreen ? '98%' : '800px'}
            >
                {parse(brokerAgreementHtml ?? '')}
            </SignaturePopup>
        </>
    );
};
